import React, { useState, useEffect, useCallback, useContext } from "react";
import { Outlet } from "react-router-dom";
import { GlobalStyles, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BrandContext } from "../../App";
import VideoAvatar from "../VideoAvatar/VideoAvatar";
import PreviewModal from "./PreviewModal.jsx";
import { useUI } from "../../contexts/UIContext";

const KEYFRAMES = (
  <GlobalStyles
    styles={`
    @keyframes mp-pulse { 0%,100%{opacity:0.4;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.1)} }
    @keyframes mp-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
    @keyframes mp-pulse-ring { 0%{transform:scale(0.95);opacity:0.7} 70%,100%{transform:scale(1.4);opacity:0} }
    @keyframes mp-blink { 0%,100%{opacity:0.4} 50%{opacity:1} }
    @keyframes mp-zoom { from{transform:scale(1)} to{transform:scale(1.08)} }
    @keyframes mp-fade-in { from{opacity:0} to{opacity:1} }
    @keyframes mp-slide-up { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }

    .mp-grid { display:grid; gap:24px; padding:8px 0 0; grid-template-columns:1fr; }
    @media(min-width:640px){ .mp-grid{ grid-template-columns:repeat(2,1fr); } }
    @media(min-width:1100px){ .mp-grid{ grid-template-columns:repeat(3,1fr); } }

    .mp-header { display:flex; justify-content:space-between; align-items:flex-end; gap:32px; padding:0px 0 20px 0; flex-wrap:wrap; }

    .mp-stats { display:flex; gap:24px; padding:16px 24px; border-radius:14px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); }
    @media(max-width:639px){ .mp-stats{ display:none !important; } }

    .mp-filter-bar { display:flex; gap:16px; padding:16px; border-radius:14px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.10); margin-top:4px; flex-wrap:wrap; }

    .mp-chips-row { display:flex; gap:8px; padding:16px 0 8px; flex-wrap:wrap; }
    @media(max-width:639px){ .mp-chips-row{ flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; padding-bottom:12px; } }
    .mp-chips-row::-webkit-scrollbar { height:0; }

    .mp-spotlight-card { display:grid; grid-template-columns:1fr 1fr; background:linear-gradient(180deg,rgba(33,31,69,0.7),rgba(33,31,69,0.4)); border-radius:18px; overflow:hidden; }
    @media(max-width:1099px){ .mp-spotlight-card{ grid-template-columns:1fr !important; } }
    @media(max-width:1099px){ .mp-spotlight-card > *:first-child{ min-height:220px; } }

    .mp-carousel { display:flex; gap:0; overflow-x:scroll; scroll-snap-type:x mandatory; -webkit-overflow-scrolling:touch; padding-bottom:8px; }
    .mp-carousel::-webkit-scrollbar { height:0; }
    .mp-carousel-item { flex:0 0 88%; scroll-snap-align:start; padding-right:12px; box-sizing:border-box; }
    .mp-carousel-item:last-child { flex:0 0 100%; padding-right:0; }


    .mp-hero-section { position:relative; border-radius:20px; padding:40px; margin-top:16px; overflow:hidden; }
    @media(max-width:1099px){ .mp-hero-section{ padding:28px !important; } }
    @media(max-width:639px){  .mp-hero-section{ padding:20px !important; } }

    .mp-hero-grid { position:relative; display:grid; grid-template-columns:1fr 1fr; gap:40px; align-items:center; }
    @media(max-width:1099px){ .mp-hero-grid{ grid-template-columns:1fr !important; gap:24px !important; } }
    @media(max-width:639px){  .mp-hero-grid{ grid-template-columns:1fr !important; gap:20px !important; } }
    @media(max-width:1099px){ .mp-hero-grid > *:first-child{ width:100%; } }

    .mp-hero-title { font-size:44px; font-weight:700; color:#FEFEFE; margin:0; line-height:1.1; letter-spacing:-0.02em; }
    @media(max-width:1099px){ .mp-hero-title{ font-size:32px !important; } }
    @media(max-width:639px){  .mp-hero-title{ font-size:26px !important; } }

    .mp-reviews-layout { display:grid; grid-template-columns:minmax(220px,280px) 1fr; gap:24px; align-items:start; }
    @media(max-width:639px){ .mp-reviews-layout{ grid-template-columns:1fr !important; } }

    .mp-about-layout { display:grid; grid-template-columns:280px 1fr; gap:40px; align-items:start; }
    @media(max-width:1099px){ .mp-about-layout{ grid-template-columns:1fr !important; } }
    @media(max-width:639px){  .mp-about-layout{ grid-template-columns:1fr !important; gap:24px !important; } }
  `}
  />
);

// ── Main ProgramMarketplace layout ───────────────────────────────────────────
const ProgramMarketplace = () => {
  const { brand } = useContext(BrandContext);
  const [previewProgram, setPreviewProgram] = useState(null);
  const [playToken, setPlayToken] = useState(null);
  const [simulationData, setSimulationData] = useState(null);
  const { setSideMenuOpen } = useUI();

  const onAskPlay = useCallback((id) => setPlayToken(id), []);
  const openPreview = (program) => setPreviewProgram(program);

  const handleSimulationStarted = (data) => {
    setPreviewProgram(null);
    setSimulationData(data);
  };

  useEffect(() => {
    setSideMenuOpen(false);
  }, []);

  return (
    <>
      {KEYFRAMES}

      <Outlet context={{ openPreview, playToken, onAskPlay, onSimulationStarted: handleSimulationStarted }} />

      {previewProgram && (
        <PreviewModal
          program={previewProgram}
          onClose={() => setPreviewProgram(null)}
          onSimulationStarted={handleSimulationStarted}
        />
      )}

      {simulationData && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,0,40,0.82)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            animation: "mp-fade-in 250ms ease",
            padding: { xs: 0, sm: "16px" },
          }}
        >
          <IconButton
            onClick={() => setSimulationData(null)}
            sx={{ position: "absolute", top: 16, right: 16, color: "white", zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              width: "100%",
              maxWidth: 1200,
              animation: "mp-slide-up 300ms cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <VideoAvatar
              scenario={{ name: simulationData?.name ?? "Demo Simulation" }}
              config={simulationData}
              simulationType="marketplace_demo"
              forceAudioOnly={simulationData?.type === "audio"}
              onClose={() => setSimulationData(null)}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ProgramMarketplace;
